import { ISO3166Alpha3 } from "@faire/web-api--source/indigofair/iso3166/ISO3166Alpha3";
import { Language } from "@faire/web-api--source/indigofair/languages/Language";
import { ILocaleKey } from "@faire/web-api--source/indigofair/locale/ILocaleKey";
import { DEFAULT_LOCALE_KEY } from "@faire/web/common/consts/DEFAULT_LOCALE";
import { THREE_TO_TWO_LETTER_LANGUAGE_CODE } from "@faire/web/common/consts/THREE_TO_TWO_LETTER_LANGUAGE_CODE";
import { TWO_TO_THREE_LETTER_COUNTRY_ISO_CODE } from "@faire/web/common/consts/TWO_TO_THREE_LETTER_COUNTRY_ISO_CODE";
import { getNavigator } from "@faire/web/common/globals/getNavigator";
import { getWindow } from "@faire/web/common/globals/getWindow";
import invert from "lodash/invert";

import { getSettingRetailerAvailableLocales } from "../settings/getSettingRetailerAvailableLocales";

export const getBrowserDefaultLanguage = (): keyof typeof Language => {
  let defaultLanguage = getWindow()?.navigator?.language?.toUpperCase() ?? "";
  if (defaultLanguage.includes("-")) {
    defaultLanguage = defaultLanguage.split("-")[0] ?? "";
  }
  return TWO_TO_THREE_LETTER_LANGUAGE_CODE[
    defaultLanguage
  ] as keyof typeof Language;
};

const TWO_TO_THREE_LETTER_LANGUAGE_CODE = invert(
  THREE_TO_TWO_LETTER_LANGUAGE_CODE
);

/**
 * Attempts to parse the string to ILocaleKey. Assumes the string is in standard
 * locale format, e.g. "fr", "fr-FR", or "fr_FR".
 * @param locale the locale string in standard locale format
 * @returns the locale key or DEFAULT_LOCALE_KEY if parsing was not successful
 * @deprecated use localeStringToLocaleKey in @faire/web/common instead
 */
export const getLocaleKeyFromLocaleString = (locale: string): ILocaleKey => {
  let defaultLanguage = locale.toUpperCase();
  let defaultCountry, splittedDefaultLanguage, splittedDefaultCountry;

  if (defaultLanguage.includes("-")) {
    [splittedDefaultLanguage, splittedDefaultCountry] =
      defaultLanguage.split("-");
  } else if (defaultLanguage.includes("_")) {
    [splittedDefaultLanguage, splittedDefaultCountry] =
      defaultLanguage.split("_");
  }

  if (splittedDefaultCountry) {
    defaultCountry = splittedDefaultCountry;
  }
  if (splittedDefaultLanguage) {
    defaultLanguage = splittedDefaultLanguage;
  }

  const threeLetterLanguage =
    TWO_TO_THREE_LETTER_LANGUAGE_CODE[defaultLanguage];
  const language = threeLetterLanguage
    ? Language[threeLetterLanguage as keyof typeof Language]
    : Language.ENG;
  const country = defaultCountry
    ? ISO3166Alpha3[
        TWO_TO_THREE_LETTER_COUNTRY_ISO_CODE[
          defaultCountry as keyof typeof TWO_TO_THREE_LETTER_COUNTRY_ISO_CODE
        ]
      ]
    : undefined;

  for (const localeKey of getSettingRetailerAvailableLocales()) {
    if (localeKey.language === language && localeKey.country === country) {
      // the locale matches a supported locale
      return { ...localeKey };
    }
  }

  if (language in Language) {
    return {
      language: language,
      country: undefined,
    };
  }

  return DEFAULT_LOCALE_KEY;
};

/**
 * Returns the locale key associated with the browser's default language.
 * This assumes the browser returns its language in on of the standard format, i.e.
 * "fr", "fr-FR", or "fr_FR".
 * @returns the ILocaleKey representation of the browser locale, or DEFAULT_LOCALE_KEY
 *  if it cannot determine the correct form
 */
export const getBrowserDefaultLocaleKey = (): ILocaleKey =>
  getLocaleKeyFromLocaleString(getNavigator()?.language ?? "");
